import React, { useState } from 'react';
import { Package, PlusCircle, Pen, List, Search, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

export const PartsSection = ({ woId, workOrder, canEditPricing, woParts, totalPartsCost, predefinedCategories, onPartsChanged }) => {
  const [showPartSearch, setShowPartSearch] = useState(false);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partQty, setPartQty] = useState('1');
  const [showCustomPartDialog, setShowCustomPartDialog] = useState(false);
  const [customPart, setCustomPart] = useState({ name: '', reference: '', brand: '', quantity: '1', sell_price: '' });
  const [customSuggestions, setCustomSuggestions] = useState([]);
  const [showPredefinedMenu, setShowPredefinedMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [predefinedPartToAdd, setPredefinedPartToAdd] = useState(null);
  const [predefinedQty, setPredefinedQty] = useState('1');
  const [predefinedPrice, setPredefinedPrice] = useState('');
  const [editingPart, setEditingPart] = useState(null);
  const [editPartForm, setEditPartForm] = useState({ name: '', quantity: '', sell_price: '', reference: '', brand: '' });

  const searchParts = async (q) => {
    setPartSearch(q);
    if (q.length < 2) { setPartResults([]); return; }
    try {
      const res = await axios.get(`${API}/inventory/parts`, { params: { search: q } });
      setPartResults(res.data);
    } catch { /* ignore */ }
  };

  const addPartToWo = (part) => {
    setSelectedPart(part);
    setPartQty('1');
    setPartResults([]);
    setPartSearch('');
  };

  const confirmAddPart = async () => {
    if (!selectedPart) return;
    const qty = parseFloat(partQty);
    if (!qty || qty <= 0) { toast.error('Quantite invalide'); return; }
    try {
      await axios.post(`${API}/work-orders/${woId}/parts`, { part_id: selectedPart.id, quantity: qty });
      toast.success(`${selectedPart.name} ajouté (x${qty})`);
      setSelectedPart(null);
      setShowPartSearch(false);
      onPartsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const addCustomPart = async () => {
    const qty = parseFloat(customPart.quantity);
    const price = canEditPricing ? parseFloat(customPart.sell_price) : 0;
    if (!customPart.name.trim()) { toast.error('Nom requis'); return; }
    if (!qty || qty <= 0) { toast.error('Quantite invalide'); return; }
    if (canEditPricing && (isNaN(price) || price < 0)) { toast.error('Prix invalide'); return; }
    try {
      await axios.post(`${API}/work-orders/${woId}/parts/custom`, {
        name: customPart.name, reference: customPart.reference, brand: customPart.brand,
        quantity: qty, sell_price: price, ...(canEditPricing ? {} : { added_by: 'atelier' }),
      });
      toast.success(`${customPart.name} ajoute`);
      setShowCustomPartDialog(false);
      setCustomPart({ name: '', reference: '', brand: '', quantity: '1', sell_price: '' });
      setCustomSuggestions([]);
      onPartsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const searchCustomSuggestions = async (q) => {
    setCustomPart(prev => ({ ...prev, name: q }));
    if (q.length < 2) { setCustomSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/work-orders/custom-parts/suggestions`, { params: { search: q } });
      setCustomSuggestions(res.data);
    } catch { setCustomSuggestions([]); }
  };

  const selectCustomSuggestion = (s) => {
    setCustomPart({ name: s.name, reference: s.reference || '', brand: s.brand || '', quantity: '1', sell_price: String(s.sell_price || '') });
    setCustomSuggestions([]);
  };

  const addPredefinedPartDirect = async (item, qty) => {
    const quantity = parseFloat(qty);
    if (!quantity || quantity <= 0) { toast.error('Quantite invalide'); return; }
    const price = canEditPricing ? (item.sell_price || 0) : 0;
    try {
      await axios.post(`${API}/work-orders/${woId}/parts/custom`, {
        name: item.name, reference: item.reference || '',
        brand: '', quantity, sell_price: price, ...(canEditPricing ? {} : { added_by: 'atelier' }),
      });
      toast.success(`${item.name} x${quantity}`);
      onPartsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const addPredefinedPart = async () => {
    if (!predefinedPartToAdd) return;
    const qty = parseFloat(predefinedQty);
    const price = canEditPricing ? parseFloat(predefinedPrice) : 0;
    if (!qty || qty <= 0) { toast.error('Quantite invalide'); return; }
    if (canEditPricing && (isNaN(price) || price < 0)) { toast.error('Prix invalide'); return; }
    try {
      await axios.post(`${API}/work-orders/${woId}/parts/custom`, {
        name: predefinedPartToAdd.name, reference: predefinedPartToAdd.reference || '',
        brand: '', quantity: qty, sell_price: price, ...(canEditPricing ? {} : { added_by: 'atelier' }),
      });
      toast.success(`${predefinedPartToAdd.name} ajoute (x${qty})`);
      setPredefinedPartToAdd(null);
      setPredefinedQty('1');
      setPredefinedPrice('');
      onPartsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removePartFromWo = async (wpId) => {
    try {
      await axios.delete(`${API}/work-orders/${woId}/parts/${wpId}`);
      toast.success('Piece retiree');
      onPartsChanged();
    } catch { toast.error('Erreur'); }
  };

  const openEditPart = (p) => {
    setEditingPart(p);
    setEditPartForm({
      name: p.name || p.part_name || '',
      quantity: String(p.quantity || 1),
      sell_price: String(p.sell_price || 0),
      reference: p.reference || '',
      brand: p.brand || '',
    });
  };

  const saveEditPart = async () => {
    if (!editingPart) return;
    const qty = parseFloat(editPartForm.quantity);
    const price = parseFloat(editPartForm.sell_price);
    if (!editPartForm.name.trim()) { toast.error('Nom requis'); return; }
    if (!qty || qty <= 0) { toast.error('Quantite invalide'); return; }
    if (isNaN(price) || price < 0) { toast.error('Prix invalide'); return; }
    try {
      await axios.put(`${API}/work-orders/${woId}/parts/${editingPart.id}`, {
        name: editPartForm.name,
        quantity: qty,
        sell_price: price,
        reference: editPartForm.reference,
        brand: editPartForm.brand,
      });
      toast.success('Piece modifiee');
      setEditingPart(null);
      onPartsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <Card className="p-4 rounded-xl space-y-3" data-testid="wo-parts-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Package className="w-4 h-4" /> Pièces utilisées
          {canEditPricing && totalPartsCost > 0 && <span className="text-blue-600 font-bold ml-2">{totalPartsCost.toFixed(2)} CHF</span>}
        </h3>
        {workOrder.status !== 'completed' && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowPredefinedMenu(!showPredefinedMenu); setShowPartSearch(false); setSelectedCategory(null); }} data-testid="predefined-parts-btn" className={showPredefinedMenu ? 'bg-blue-50 border-blue-300' : ''}>
              <List className="w-3.5 h-3.5 mr-1" /> Pieces courantes
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowCustomPartDialog(true)} data-testid="add-custom-part-btn">
              <Pen className="w-3.5 h-3.5 mr-1" /> Piece libre
            </Button>
            {canEditPricing && (
              <Button variant="outline" size="sm" onClick={() => { setShowPartSearch(!showPartSearch); setShowPredefinedMenu(false); }} data-testid="add-part-btn">
                <PlusCircle className="w-4 h-4 mr-1" /> Stock
              </Button>
            )}
          </div>
        )}
      </div>

      {showPredefinedMenu && (
        <div className="border rounded-lg bg-slate-50 p-3 space-y-2" data-testid="predefined-menu">
          <div className="flex flex-wrap gap-1.5">
            {predefinedCategories.map((cat, i) => (
              <button key={i} type="button" onClick={() => setSelectedCategory(selectedCategory === cat.category ? null : cat.category)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selectedCategory === cat.category ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                data-testid={`cat-${cat.category.replace(/\s/g,'-').toLowerCase()}`}>
                {cat.category}
              </button>
            ))}
          </div>
          {selectedCategory && (
            <div className="space-y-1.5 mt-2">
              {predefinedCategories.find(c => c.category === selectedCategory)?.items.map((item, j) => (
                <div key={j} className="flex items-center gap-2 px-3 py-2 bg-white rounded border border-slate-200 hover:border-blue-300 transition-colors"
                  data-testid={`predef-${item.reference}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-slate-800 block">{item.name}</span>
                    <span className="text-[10px] text-slate-400">{item.reference}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Label className="text-[10px] text-slate-500">Qte:</Label>
                    <Input
                      type="number" min="1" step="1" defaultValue="1"
                      className="w-14 h-7 text-xs text-center px-1"
                      data-testid={`predef-qty-${item.reference}`}
                      id={`predef-qty-${item.reference}`}
                    />
                    <Button size="sm" className="h-7 px-2 text-xs"
                      onClick={() => {
                        const qtyInput = document.getElementById(`predef-qty-${item.reference}`);
                        const qty = qtyInput ? qtyInput.value : '1';
                        addPredefinedPartDirect(item, qty);
                      }}
                      data-testid={`predef-add-${item.reference}`}>
                      <PlusCircle className="w-3 h-3 mr-0.5" />Ajouter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {predefinedPartToAdd && (
        <Dialog open={!!predefinedPartToAdd} onOpenChange={() => setPredefinedPartToAdd(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{predefinedPartToAdd.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Quantite</Label><Input type="number" step="0.1" min="0.1" value={predefinedQty} onChange={(e) => setPredefinedQty(e.target.value)} autoFocus data-testid="predef-qty" /></div>
              {canEditPricing && (
                <div><Label>Prix unitaire (CHF)</Label><Input type="number" step="0.01" min="0" value={predefinedPrice} onChange={(e) => setPredefinedPrice(e.target.value)} placeholder="0.00" data-testid="predef-price" /></div>
              )}
              {canEditPricing && predefinedQty && predefinedPrice && parseFloat(predefinedQty) > 0 && parseFloat(predefinedPrice) >= 0 && (
                <p className="text-sm font-bold text-blue-600">Total: {(parseFloat(predefinedQty) * parseFloat(predefinedPrice)).toFixed(2)} CHF</p>
              )}
              <Button className="w-full" onClick={addPredefinedPart} data-testid="confirm-predef-btn">Ajouter</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showPartSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher une pièce (nom, réf., marque)..." value={partSearch} onChange={(e) => searchParts(e.target.value)} className="pl-10" data-testid="part-search-input" autoFocus />
          {partResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {partResults.map(p => (
                <button key={p.id} type="button" className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm flex items-center justify-between" onClick={() => addPartToWo(p)} data-testid={`part-result-${p.id}`}>
                  <div>
                    <span className="font-medium">{p.name}</span>
                    {p.brand && <span className="text-slate-400 ml-1">({p.brand})</span>}
                    <span className="text-xs text-slate-400 ml-2">{p.reference}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-slate-900">{p.sell_price?.toFixed(2)} CHF</span>
                    <span className="text-xs text-slate-400 ml-2">Stock: {p.quantity}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedPart && (
        <Dialog open={!!selectedPart} onOpenChange={() => setSelectedPart(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Quantite - {selectedPart.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                {canEditPricing && <>Prix unitaire: <span className="font-bold">{selectedPart.sell_price?.toFixed(2)} CHF</span></>}
                <span className="ml-2 text-xs">(Stock: {selectedPart.quantity})</span>
              </p>
              <div><Label>Quantite</Label><Input type="number" step="0.1" min="0.1" value={partQty} onChange={(e) => setPartQty(e.target.value)} data-testid="part-qty-input" autoFocus /></div>
              {partQty && parseFloat(partQty) > 0 && (
                <p className="text-sm font-bold text-blue-600">Total: {(parseFloat(partQty) * selectedPart.sell_price).toFixed(2)} CHF HT</p>
              )}
              <Button className="w-full" onClick={confirmAddPart} data-testid="confirm-add-part-btn">Ajouter</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={showCustomPartDialog} onOpenChange={(open) => { setShowCustomPartDialog(open); if (!open) setCustomSuggestions([]); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ajouter une pièce libre</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Label>Designation *</Label>
              <Input placeholder="Ex: Joint de culasse, Liquide frein..." value={customPart.name} onChange={(e) => searchCustomSuggestions(e.target.value)} data-testid="custom-part-name" autoFocus />
              {customSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {customSuggestions.map((s, i) => (
                    <button key={i} type="button" className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center justify-between" onClick={() => selectCustomSuggestion(s)} data-testid={`custom-suggestion-${i}`}>
                      <div><span className="font-medium">{s.name}</span>{s.brand && <span className="text-slate-400 ml-1 text-xs">({s.brand})</span>}</div>
                      <span className="text-xs font-bold text-slate-600">{s.sell_price?.toFixed(2)} CHF</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Reference</Label><Input placeholder="Ref." value={customPart.reference} onChange={(e) => setCustomPart({...customPart, reference: e.target.value})} data-testid="custom-part-ref" /></div>
              <div><Label>Marque</Label><Input placeholder="Marque" value={customPart.brand} onChange={(e) => setCustomPart({...customPart, brand: e.target.value})} data-testid="custom-part-brand" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Quantite *</Label><Input type="number" step="0.1" min="0.1" value={customPart.quantity} onChange={(e) => setCustomPart({...customPart, quantity: e.target.value})} data-testid="custom-part-qty" /></div>
              {canEditPricing && (
                <div><Label>Prix unitaire CHF *</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={customPart.sell_price} onChange={(e) => setCustomPart({...customPart, sell_price: e.target.value})} data-testid="custom-part-price" /></div>
              )}
            </div>
            {canEditPricing && customPart.quantity && customPart.sell_price && parseFloat(customPart.quantity) > 0 && parseFloat(customPart.sell_price) > 0 && (
              <div className="text-sm space-y-0.5 bg-slate-50 p-2 rounded">
                <p>Total HT: <span className="font-bold">{(parseFloat(customPart.quantity) * parseFloat(customPart.sell_price)).toFixed(2)} CHF</span></p>
                <p className="text-xs text-slate-500">TVA 8.1%: {(parseFloat(customPart.quantity) * parseFloat(customPart.sell_price) * 0.081).toFixed(2)} CHF</p>
                <p>Total TTC: <span className="font-bold text-blue-600">{(parseFloat(customPart.quantity) * parseFloat(customPart.sell_price) * 1.081).toFixed(2)} CHF</span></p>
              </div>
            )}
            <Button className="w-full" onClick={addCustomPart} data-testid="confirm-custom-part-btn">Ajouter la pièce</Button>
          </div>
        </DialogContent>
      </Dialog>

      {woParts.length > 0 ? (
        <div className="space-y-1.5">
          {woParts.map(p => {
            const hasNoPrice = canEditPricing && (!p.sell_price || p.sell_price <= 0);
            const isFromAtelier = !!p.added_by;
            return (
              <div key={p.id} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${p.returned ? 'opacity-50' : ''} ${hasNoPrice ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`wo-part-${p.id}`}>
                <div className="flex-1">
                  <span className={`font-medium text-slate-900 ${p.returned ? 'line-through' : ''}`}>{p.name || p.part_name}</span>
                  {p.returned && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-2">Retour – non facturé</span>}
                  {p.source === 'bulletin' && <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded ml-2">Bulletin {p.slip_number}</span>}
                  {p.brand && <span className="text-slate-400 ml-1">({p.brand})</span>}
                  {p.reference && <span className="text-xs text-slate-400 ml-2">{p.reference}</span>}
                  {p.is_custom && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded ml-2">Libre</span>}
                  {isFromAtelier && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded ml-2">Atelier</span>}
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 text-xs font-semibold text-red-700 cursor-pointer">
                    <input type="checkbox" checked={!!p.returned} data-testid={`part-return-${p.id}`} onChange={async (e) => {
                      try { await axios.put(`${API}/work-orders/${woId}/parts/${p.id}/returned`, { returned: e.target.checked }); onPartsChanged(); }
                      catch { toast.error('Erreur'); }
                    }} />Retour
                  </label>
                  <span className="text-xs text-slate-500">x{p.quantity}</span>
                  {canEditPricing && hasNoPrice && workOrder.status !== 'completed' && (
                    <input type="number" step="0.01" min="0" placeholder="Prix CHF"
                      className="w-24 h-7 px-2 text-xs border border-amber-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                      data-testid={`set-price-${p.id}`}
                      onBlur={async (e) => {
                        const price = parseFloat(e.target.value);
                        if (isNaN(price) || price <= 0) return;
                        try {
                          await axios.put(`${API}/work-orders/${woId}/parts/${p.id}/price`, { sell_price: price });
                          onPartsChanged();
                        } catch { /* ignore */ }
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          const price = parseFloat(e.target.value);
                          if (isNaN(price) || price <= 0) return;
                          try {
                            await axios.put(`${API}/work-orders/${woId}/parts/${p.id}/price`, { sell_price: price });
                            onPartsChanged();
                          } catch { /* ignore */ }
                        }
                      }}
                    />
                  )}
                  {canEditPricing && !hasNoPrice && <span className="text-xs text-slate-400">@{(p.sell_price || 0).toFixed(2)}</span>}
                  {canEditPricing && !hasNoPrice && <span className="font-bold text-slate-900">{(p.quantity * (p.sell_price || 0)).toFixed(2)} CHF</span>}
                  {hasNoPrice && <span className="text-xs text-amber-600 font-medium">Prix a confirmer</span>}
                  {canEditPricing && workOrder.status !== 'completed' && (
                    <button onClick={() => openEditPart(p)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-part-${p.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {workOrder.status !== 'completed' && (
                    <button onClick={() => removePartFromWo(p.id)} className="text-red-400 hover:text-red-600" data-testid={`remove-part-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-slate-400">Aucune piece ajoutee</p>
      )}

      {/* Edit part dialog */}
      {editingPart && (
        <Dialog open={!!editingPart} onOpenChange={() => setEditingPart(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Modifier la piece</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Designation</Label>
                <Input value={editPartForm.name} onChange={e => setEditPartForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-part-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Reference</Label>
                  <Input value={editPartForm.reference} onChange={e => setEditPartForm(f => ({ ...f, reference: e.target.value }))} data-testid="edit-part-ref" />
                </div>
                <div>
                  <Label>Marque</Label>
                  <Input value={editPartForm.brand} onChange={e => setEditPartForm(f => ({ ...f, brand: e.target.value }))} data-testid="edit-part-brand" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Quantite</Label>
                  <Input type="number" step="0.1" min="0.1" value={editPartForm.quantity} onChange={e => setEditPartForm(f => ({ ...f, quantity: e.target.value }))} data-testid="edit-part-qty" />
                </div>
                <div>
                  <Label>Prix unitaire CHF</Label>
                  <Input type="number" step="0.01" min="0" value={editPartForm.sell_price} onChange={e => setEditPartForm(f => ({ ...f, sell_price: e.target.value }))} data-testid="edit-part-price" />
                </div>
              </div>
              {editPartForm.quantity && editPartForm.sell_price && parseFloat(editPartForm.quantity) > 0 && parseFloat(editPartForm.sell_price) > 0 && (
                <p className="text-sm font-bold text-blue-600">Total: {(parseFloat(editPartForm.quantity) * parseFloat(editPartForm.sell_price)).toFixed(2)} CHF</p>
              )}
              <Button className="w-full" onClick={saveEditPart} data-testid="save-edit-part-btn">Enregistrer les modifications</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
